import { formatCurrency } from '@if/core/utils'
import { DocumentField, Help, Icon, SummaryCard } from '@if/mui'
import { Stack } from '@mui/material'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { Banknote, CircleCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useLoanCalendarNearestOperationInformation,
  useLoanCurrentBalance,
  useLoanSummaryInformation,
} from '../LoanCalendar-hooks'

interface Props {
  simulation: CalendarSimulation
}

const LoanStatusCard = ({ simulation }: Props) => {
  const { t } = useTranslation()
  const nearestOperationInfo = useLoanCalendarNearestOperationInformation(simulation)
  const currentBalance = useLoanCurrentBalance(simulation)
  const summaryInformation = useLoanSummaryInformation(simulation)

  const dailyInterest = summaryInformation?.dailyInterest ?? 0
  const lateCapital = summaryInformation?.pastduePrincipal ?? 0
  const pastdueAndAccruedInterest = summaryInformation?.pastdueAndAccruedInterest ?? 0
  const outstandingBalance = summaryInformation?.outstandingBalance ?? 0
  const amountRequiredForPayout = summaryInformation?.amountRequiredForPayout ?? 0
  const amountInCompensation = summaryInformation?.amountInCompensation ?? 0

  const nsfBalance = nearestOperationInfo?.operationResultDto.nsfBalance ?? 0
  const otherBalance = nearestOperationInfo?.operationResultDto.otherBalance ?? 0
  const totalLateAndFee = pastdueAndAccruedInterest + nsfBalance + otherBalance
  const lateAccruedInterest = pastdueAndAccruedInterest - lateCapital

  const unscheduledAmount =
    outstandingBalance > 0 ? (
      formatCurrency(outstandingBalance)
    ) : (
      <Stack component="span" direction="row" spacing={0.5} alignItems="center">
        <Icon icon={CircleCheck} size="sm" />
        <span>{t('loan.payment.statusCard.status.upToDate')}</span>
      </Stack>
    )

  return (
    <SummaryCard icon={Banknote} title={t('loan.payment.statusCard.title')} dense>
      <DocumentField
        label={t('loan.payment.statusCard.total')}
        align="right"
        value={formatCurrency(currentBalance)}
        strong
      />
      <DocumentField
        label={
          <Stack component="span" direction="row" spacing={0.5} alignItems="center">
            <span>{t('loan.payment.statusCard.compensation')}</span>
            {amountInCompensation === 0 && <Help title={t('loan.payment.statusCard.compensationEmptyTooltip')} />}
          </Stack>
        }
        align="right"
        value={formatCurrency(amountInCompensation)}
      />
      <DocumentField
        label={t('loan.payment.statusCard.dailyInterest')}
        align="right"
        value={formatCurrency(dailyInterest)}
      />
      <DocumentField
        label={t('loan.payment.statusCard.lateBalance')}
        align="right"
        value={formatCurrency(totalLateAndFee)}
        valueColor={totalLateAndFee > 0 ? 'error' : undefined}
        strong
      >
        <DocumentField
          label={t('loan.payment.statusCard.lateBalanceTooltip.principal')}
          value={formatCurrency(lateCapital)}
        />
        <DocumentField
          label={t('loan.payment.statusCard.lateBalanceTooltip.interest')}
          value={formatCurrency(lateAccruedInterest)}
        />
        <DocumentField
          label={t('loan.payment.statusCard.lateBalanceTooltip.nsfFee')}
          value={formatCurrency(nsfBalance)}
        />
        <DocumentField
          label={t('loan.payment.statusCard.lateBalanceTooltip.otherFee')}
          value={formatCurrency(otherBalance)}
        />
      </DocumentField>
      <DocumentField
        label={t('loan.payment.statusCard.amountRequiredForPayout')}
        align="right"
        value={formatCurrency(amountRequiredForPayout)}
        strong
      />
      <DocumentField
        label={t('loan.payment.statusCard.amountNotReplaced')}
        align="right"
        value={unscheduledAmount}
        valueColor={outstandingBalance > 0 ? 'error' : 'success'}
        strong
      />
    </SummaryCard>
  )
}

export default LoanStatusCard
